/**
 * employeeOperationsMetrics — the reading half of the per-employee operations metrics panel
 * (FULL-0128 / PROD-OPS-042).
 *
 * MASTER_FLOW §15A asks an agent console to make the employee measurable, not just observable:
 * run volume, success/failure, latency, cost and confidence for one named employee, with
 * unavailable telemetry kept distinct from zero. The server half already exists behind the same
 * `agents_view` gate as every other operations read:
 *
 *     GET /api/agent-runs/stats?agent_id=…&since_days=…      (agent_runs.get_run_stats)
 *
 * and it had no per-employee consumer: `AgentRunsDashboardPage` reads it fleet-wide, while the
 * employee page's `StatsRow` counts only the visible page of recent runs. Those are two different
 * statements — a page sample and a server aggregate — and this module exists so the operations
 * screen can say the second one without quietly presenting it as the first.
 *
 * TRUTHFULNESS RULES (pinned by tests):
 *   - a count the server sent as `0` is a measured zero and renders "0";
 *   - a nullable aggregate the server sent as `null` (`avg_confidence`, `avg_duration_ms`,
 *     `total_cost_usd`, the token sums, the averages) is NOT zero; it renders "לא דווח";
 *   - a success rate with a zero or unknown denominator is undefined, not 0%;
 *   - an analysis denominator of zero is "no analyses in this window", not a degraded share of 0.
 *
 * The module reads, formats and returns data. It fetches nothing, writes nothing and grants
 * nothing; the panel that renders it imports no mutating API.
 */

/** The window the panel asks the server for. Fixed so the screen has no control that could be
 * mistaken for an operation; the window is disclosed on screen. */
export const OPERATIONS_METRICS_WINDOW_DAYS = 30;

/** The one word for a value the source did not report. Never used for a reported zero. */
export const OPERATIONS_METRIC_UNKNOWN_HE = "לא דווח";

/** A rate whose denominator is absent or zero is undefined — it is not 0%. */
export const OPERATIONS_METRIC_UNDEFINED_RATE_HE = "—";

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Normalise one `AgentRunStats` payload into the panel's reading.
 *
 * Returns `null` when no payload was supplied at all (the panel says the source did not answer);
 * every individual field is independently nullable, because one missing aggregate must not turn
 * the other counters into "unknown".
 */
export function readOperationsMetrics(stats) {
  if (!stats || typeof stats !== "object") return null;

  const total = finiteNumber(stats.total);
  const completed = finiteNumber(stats.completed);
  const rejected = finiteNumber(stats.rejected);
  const pendingReview = finiteNumber(stats.pending_review);
  const avgConfidence = finiteNumber(stats.avg_confidence);
  const avgDurationMs = finiteNumber(stats.avg_duration_ms);
  const totalCostUsd = finiteNumber(stats.total_cost_usd);
  const inputTokens = finiteNumber(stats.total_input_tokens);
  const outputTokens = finiteNumber(stats.total_output_tokens);
  const analysesTotal = finiteNumber(stats.analyses_total);
  const degradedAnalyses = finiteNumber(stats.degraded_analyses);

  return {
    total,
    completed,
    rejected,
    pendingReview,
    // A rate is a fact only when the denominator is a positive count.
    successRate: total != null && completed != null && total > 0 ? completed / total : null,
    avgConfidence,
    avgDurationMs,
    totalCostUsd,
    inputTokens,
    outputTokens,
    analysesTotal,
    degradedAnalyses,
  };
}

export function formatMetricCount(value) {
  return value == null ? OPERATIONS_METRIC_UNKNOWN_HE : String(value);
}

export function formatMetricPercent(rate) {
  if (rate == null) return OPERATIONS_METRIC_UNDEFINED_RATE_HE;
  return `${Math.round(rate * 100)}%`;
}

/** A confidence is a 0–100 score on the analysis payload; a missing one is not zero. */
export function formatMetricConfidence(value) {
  return value == null ? OPERATIONS_METRIC_UNKNOWN_HE : String(Math.round(value));
}

export function formatMetricDuration(ms) {
  if (ms == null) return OPERATIONS_METRIC_UNKNOWN_HE;
  if (ms < 1000) return `${Math.round(ms)} מ״ש`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} שנ׳`;
  return `${(seconds / 60).toFixed(1)} דק׳`;
}

export function formatMetricUsd(value) {
  return value == null ? OPERATIONS_METRIC_UNKNOWN_HE : `$${value.toFixed(2)}`;
}

/** Input/output token sums are two independently nullable server sums. */
export function formatMetricTokens(inputTokens, outputTokens) {
  if (inputTokens == null && outputTokens == null) return OPERATIONS_METRIC_UNKNOWN_HE;
  const read = (value) => (value == null ? OPERATIONS_METRIC_UNKNOWN_HE : String(value));
  return `${read(inputTokens)} / ${read(outputTokens)}`;
}

/**
 * The analysis provenance sentence.
 *
 * `analyses_total` is the denominator of every analysis-derived aggregate; `degraded_analyses`
 * counts the analyses whose recorded arm is not a real diagnosis (the pilot's mock adapter is the
 * shipped example). A consumer that printed the averages without this pair would present statistics
 * of the fallback as a reading of the tickets.
 */
export function degradedAnalysesNote(analysesTotal, degradedAnalyses) {
  if (analysesTotal == null || degradedAnalyses == null) return null;
  if (analysesTotal === 0) return "אין ניתוחים בחלון הזה — אין נתח מדורג לדווח";
  return `ניתוחים מדורגים: ${degradedAnalyses} מתוך ${analysesTotal} אינם אבחון אמיתי`;
}

/** True only when the payload reports a positive number of degraded analyses. */
export function hasDegradedAnalyses(analysesTotal, degradedAnalyses) {
  return (
    analysesTotal != null &&
    degradedAnalyses != null &&
    analysesTotal > 0 &&
    degradedAnalyses > 0
  );
}
